import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MapPin, ChevronRight, Phone } from "lucide-react";
import { ZONES, SERVICES, CHANTIERS } from "../data/siteData";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, CtaBand } from "../components/PageHeader";

export default function ZonePage() {
  const { slug } = useParams();
  const z = ZONES.find((x) => x.slug === slug);
  const title = z ? `Électricien BTP ${z.nom} (${z.code})` : "Zones d'intervention";
  useSeo(title, z ? `A.C. ELEC 94, électricien courants forts pour le BTP ${z.dans} (${z.code}) : raccordement TGBT, chemins de câbles, tirage CFO, appareillages. Devis sous 48h.` : "", `/electricien-btp/${slug}`);
  if (!z) return <Navigate to="/" replace />;
  const chantiers = CHANTIERS.filter((c) => c.dept === z.code);

  return (
    <main data-testid={`zone-page-${z.slug}`} className="pt-20">
      <PageHeader crumbs={[{ label: "Zones d'intervention" }, { label: z.nom }]} label="Zone d'intervention" title={title}>
        <p className="text-white/70 text-lg max-w-3xl animate-fade-in-up animation-delay-100">{z.intro}</p>
      </PageHeader>
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 space-y-20">
          <div className="reveal">
            <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3] mb-8">Nos prestations courants forts {z.dans}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`zone-service-${s.slug}`} className="service-card group bg-[#141210] border border-[#2E2820] p-7">
                  <h3 className="font-['Oswald'] text-xl font-bold text-[#F5EFE3] mb-3 flex items-center justify-between">{s.titre}<ChevronRight className="w-5 h-5 text-[#D4A537] group-hover:translate-x-1 transition-transform" /></h3>
                  <p className="text-[#A8A091] text-sm">{s.short}</p>
                </Link>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="reveal">
              <h2 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-6">Communes desservies</h2>
              <ul className="grid grid-cols-2 gap-3">
                {z.villes.map((v) => <li key={v} className="flex items-center gap-2 text-[#A8A091]"><MapPin className="w-4 h-4 text-[#D4A537]" />{v}</li>)}
              </ul>
              <p className="text-[#8C8578] text-sm mt-6">… et l'ensemble des communes du département.</p>
            </div>
            <div className="reveal">
              <h2 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-6">{chantiers.length ? `Nos chantiers ${z.dans}` : "Intervention rapide"}</h2>
              {chantiers.length ? (
                <div className="space-y-3">
                  {chantiers.map((c) => (
                    <Link key={c.slug} to={`/realisations/${c.slug}`} data-testid={`zone-chantier-${c.slug}`} className="flex items-center justify-between border border-[#2E2820] hover:border-[#D4A537]/60 bg-[#141210] p-4 transition-colors">
                      <span className="text-[#F5EFE3] font-semibold">{c.chantier}</span>
                      <span className="text-sm text-[#D4A537]">{c.operation}</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-[#A8A091]">Depuis le Val-de-Marne, nos équipes se mobilisent rapidement sur vos chantiers {z.dans}.</p>
              )}
              <a href="tel:0664189291" className="mt-6 inline-flex items-center gap-2 text-[#D4A537] font-semibold"><Phone className="w-4 h-4" />06 64 18 92 91</a>
            </div>
          </div>
          <div className="reveal">
            <h2 className="font-['Oswald'] text-xl font-bold text-[#F5EFE3] mb-4">Autres départements</h2>
            <div className="flex flex-wrap gap-3">
              {ZONES.filter((x) => x.slug !== z.slug).map((x) => (
                <Link key={x.slug} to={`/electricien-btp/${x.slug}`} className="border border-[#2E2820] hover:border-[#D4A537] hover:text-[#D4A537] text-[#A8A091] px-4 py-2 text-sm transition-colors">{x.nom} ({x.code})</Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <CtaBand title={`Un chantier ${z.dans} ?`} />
    </main>
  );
}
